import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { getItem } from "@Utils/browserStorage";
import { convertToDollar } from "@Utils/currency";
import { isCanadianLocale } from "@Utils/locale";
import { localeSelector } from "@wff/store/localeSlice";
import { cartDrawerActions, cartDrawerSelector } from "store/cartDrawerSlice";
import { cartQuantitySelector, cartSliceSelector } from "store/cartSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import CartDrawerContent from "./content/CartDrawerContent";
import CartDrawerFooter from "./footer/CartDrawerFooter";
import CartDrawerHeader from "./header/CartDrawerHeader";
import CartDrawerMessage from "./message/CartDrawerMessage";
import classes from "./CartDrawer.module.scss";

export const CART_DRAWER_ID = "cart-drawer";
interface ICartDrawerProps {
  pageTitle: string;
  productSKU: string;
}

export default function CartDrawer(props: ICartDrawerProps) {
  const cartSlice = useAppSelector(cartSliceSelector);
  const currentLocale = useAppSelector(localeSelector);
  const totalItemCount = useAppSelector(cartQuantitySelector);
  const { open } = useAppSelector(cartDrawerSelector);
  const { isPickupAvailable, availability } = useAppSelector(
    (state) => state?.zipcode
  );
  const dispatch = useAppDispatch();
  const onClose = () => dispatch(cartDrawerActions.toggleCartDrawer());
  const mobile = useMediaQuery("(max-width:60em)");
  const { pageTitle, productSKU } = props;

  const orderMinimumAmount = convertToDollar(
    cartSlice.shippingEligibility?.eligibilityThreshold
  );
  const amountToReachOrderMinimum = convertToDollar(
    cartSlice.shippingEligibility?.eligibilityAmountRemaining
  );

  const showOrderMinimumUnmetSection =
    !Number.isNaN(Number(amountToReachOrderMinimum)) &&
    Number(amountToReachOrderMinimum) > 0;

  const zipCodeAvailabilityInLocalStorageForCA = getItem(
    STORAGE_KEYS.CUSTOMER_ZIP_AVAILABILITY_CA,
    STORAGE_TYPES.LOCAL
  );

  const isDeliveryAvailable =
    availability || zipCodeAvailabilityInLocalStorageForCA === "true";

  const isCheckoutButtonEnabled =
    (isPickupAvailable || isDeliveryAvailable) && !showOrderMinimumUnmetSection;

  const subTotal = isCanadianLocale(currentLocale.languageCode)
    ? cartSlice?.rawSubTotal
    : cartSlice?.subTotal;
  const subTotalOrZero = subTotal ? subTotal.toFixed(2) : "0.00"; // checks if there's a subtotal or sets it to 0.00
  const hasItems = totalItemCount > 0;

  return (
    <Drawer
      anchor={"right"}
      data-testid={CART_DRAWER_ID}
      open={open}
      onClose={onClose}
      className={classes["MuiDrawer-paper"]}
      BackdropProps={{ style: { backgroundColor: "unset" } }}
      PaperProps={{
        sx: {
          width: mobile ? "100%" : "420px",
          //minHeight: "100vh",
          left: "unset",
        },
      }}
      ModalProps={{ onBackdropClick: () => onClose }}
    >
      <div className={classes["cart-flyout"]}>
        <div className={classes["cart-flyout__content"]}>
          <CartDrawerHeader
            totalItemCount={totalItemCount}
            subtotal={subTotalOrZero}
            onClose={onClose}
          />
          {hasItems && (
            <CartDrawerMessage
              showOrderMinimumUnmetSection={showOrderMinimumUnmetSection}
              orderMinimumAmount={orderMinimumAmount}
              amountToReachOrderMinimum={amountToReachOrderMinimum}
              isDeliveryAvailable={isDeliveryAvailable}
            />
          )}
          <CartDrawerContent
            onClose={onClose}
            productList={cartSlice.lineItems}
            pageTitle={pageTitle}
            productSKU={productSKU}
          />
          <CartDrawerFooter
            showFooter={hasItems}
            disableCheckout={!isCheckoutButtonEnabled}
          />
        </div>
      </div>
    </Drawer>
  );
}
